'use client';
// X402Radar — Agent Radar for the founder's x402 Payments console (v2.17.0).
//
// Founder mandate: "أنا أراقب خدماتي وأتصنّت عليها — من رأى، من لم يدفع،
// وأي الوكلاء أكثر اهتماماً بأي خدمة". This panel turns the durable
// request tracker (db/x402-requests.json) into a live radar:
//
//   1. FUNNEL      — discovery probes → 402 quotes → settled payments
//   2. MOST WANTED — per-service demand: quotes, payments, conversion,
//                    unique callers, last activity
//   3. WARM LEADS  — who SAW the services and never paid (quoted-but-unpaid
//                    first, discovery-only viewers second)
//   4. AGENT BOARD — most interested callers ranked by real interest
//                    (payments > quotes > probes), with their user-agent
//                    and the service they keep coming back to
//   5. 48H PULSE   — hourly activity bars with paid overlays
//   6. LIVE FEED   — the raw tracker stream, newest first, honest badges
//
// Strictly read-only: this component never signs, moves or mints anything.
import { useState } from 'react';
import {
  Radar, Eye, HandCoins, Bot, Flame, TrendingUp, Activity,
  Radio, UserX, Globe, CircleAlert, CheckCircle2, Hourglass,
  Stethoscope, Lightbulb, Scale, SearchX, Undo2,
} from 'lucide-react';
import { fmtInt, Mono, timeAgo, StatCard } from './shared';

// ─── API shapes (mirror src/lib/chain/bazaar.ts) ───
export interface X402ServiceInterest {
  serviceId: string;
  quotes: number; settled: number; nativeSettled: number; failed: number;
  paidTotal: number; conversionPct: number; uniqueCallers: number;
  earnedMicros: number; earnedAman: number; lastAt: number | null;
}
export interface X402CallerProfile {
  callerHash: string;
  userAgent: string | null;
  clientKind: 'browser' | 'agent' | 'unknown';
  hits: number; quotes: number; settled: number; nativeSettled: number;
  failed: number; discoveries: number;
  paidMicros: number; paidAman: number;
  services: string[]; topService: string | null;
  paid: boolean; firstAt: number; lastAt: number;
}
export interface X402Analytics {
  generatedAt: number;
  funnel: { discovery: number; quotes: number; verifyFailed: number; settled: number; nativeSettled: number; errors: number };
  perService: X402ServiceInterest[];
  callers: X402CallerProfile[];
  unpaid: X402CallerProfile[];
  viewers: X402CallerProfile[];
  activity48h: Array<{ hour: string; hits: number; paid: number }>;
}
export interface X402FeedEntry {
  ts: number;
  route: string;
  serviceId: string | null;
  kind: 'quote' | 'verify-failed' | 'settled' | 'native-settled' | 'catalog' | 'error' | 'discovery' | 'refunded';
  surface?: string;
  payer: string | null;
  amountMicros?: string;
  amountAman?: number;
  bazaar?: string | null;
  userAgent: string | null;
  callerHash: string | null;
  clientKind?: 'browser' | 'agent' | 'unknown';
}

// ─── v2.18.0 — why-didn't-they-buy diagnostics (mirror bazaar.ts) ───
export interface WhyNotPaidReason { code: string; label: string; evidence: string }
export interface WhyNotPaidClientDiag {
  callerHash: string;
  userAgent: string | null;
  clientKind: 'browser' | 'agent' | 'unknown';
  quotes: number; failed: number; discoveries: number;
  servicesQuoted: string[]; topService: string | null;
  firstAt: number; lastAt: number; idleDays: number;
  reasons: WhyNotPaidReason[]; verdict: string;
}
export interface WhyNotPaidServiceDiag {
  serviceId: string; quotes: number; failed: number; paidTotal: number; conversionPct: number;
  priceAman: number | null; priceUsd: number | null; suggestedPriceAman: number | null;
  diagnosis: string; action: string;
}
export interface X402WhyNotPaidDiag {
  generatedAt: number;
  unpaidTotal: number;
  reasonCounts: Array<{ code: string; label: string; count: number }>;
  clients: WhyNotPaidClientDiag[];
  services: WhyNotPaidServiceDiag[];
  summary: string;
}

const KIND_META: Record<X402FeedEntry['kind'], { label: string; cls: string; icon: typeof Eye }> = {
  settled:        { label: 'مدفوعة · USDC',   cls: 'bg-sky-500/10 text-sky-400',        icon: CheckCircle2 },
  'native-settled': { label: 'مدفوعة · AMAN', cls: 'bg-emerald-500/10 text-emerald-400', icon: CheckCircle2 },
  refunded:       { label: 'مُستردة · فشل تنفيذ', cls: 'bg-amber-500/10 text-amber-400', icon: Undo2 },
  quote:          { label: 'طلب سعر 402',     cls: 'bg-amber-500/10 text-amber-400',    icon: Hourglass },
  'verify-failed':{ label: 'فشل تحقق',        cls: 'bg-rose-500/10 text-rose-400',      icon: CircleAlert },
  discovery:      { label: 'اكتشاف',          cls: 'bg-violet-500/10 text-violet-400',  icon: Radar },
  catalog:        { label: 'كتالوج',          cls: 'bg-violet-500/10 text-violet-400',  icon: Radar },
  error:          { label: 'خطأ',             cls: 'bg-rose-500/10 text-rose-400',      icon: CircleAlert },
};

const SURFACE_LABEL: Record<string, string> = {
  'well-known-x402': '/.well-known/x402',
  'services-json': '/services.json',
  'llms-txt': '/llms.txt',
  'agent-json': '/.well-known/agent.json',
  'bazaar-directory': '/x402/discovery',
  'catalog': '/api/x402 (GET)',
};

function ClientKindBadge({ kind }: { kind: 'browser' | 'agent' | 'unknown' | undefined }) {
  if (kind === 'agent') return <span className="rounded-full bg-emerald-500/10 text-emerald-400 px-2 py-0.5 text-[0.56rem] font-black">وكيل</span>;
  if (kind === 'browser') return <span className="rounded-full bg-sky-500/10 text-sky-400 px-2 py-0.5 text-[0.56rem] font-black">متصفح</span>;
  return <span className="rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[0.56rem] font-bold">؟</span>;
}

function uaShort(ua: string | null): string {
  if (!ua) return '—';
  const s = ua.replace(/\s+/g, ' ').trim();
  return s.length > 42 ? `${s.slice(0, 42)}…` : s;
}

/** 48h pulse — pure CSS bars, no chart lib, honest to the tracker data. */
function PulseBars({ data }: { data: Array<{ hour: string; hits: number; paid: number }> }) {
  if (data.length === 0) {
    return <div className="text-xs text-muted-foreground">لا نشاط مسجل في آخر 48 ساعة بعد — أول استكشاف من أي وكيل سيظهر هنا فوراً.</div>;
  }
  const max = Math.max(...data.map(d => d.hits), 1);
  return (
    <div>
      <div className="flex items-end gap-[3px] h-20" dir="ltr">
        {data.map(d => (
          <div key={d.hour} className="flex-1 min-w-[6px] flex flex-col justify-end items-stretch gap-[1px] group relative" title={`${d.hour} — ${d.hits} hit(s), ${d.paid} paid`}>
            {d.paid > 0 && (
              <div className="bg-sky-400 rounded-t-[2px]" style={{ height: `${Math.max(8, (d.paid / max) * 76)}%` }} />
            )}
            <div className={`${d.paid > 0 ? 'bg-emerald-400/40' : 'bg-emerald-500/25'} group-hover:bg-emerald-400 transition-colors rounded-t-[2px]`}
              style={{ height: `${Math.max(6, (d.hits / max) * 76)}%` }} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[0.6rem] text-muted-foreground mt-1" dir="ltr">
        <span>{data[0]?.hour}</span>
        <span>{data[data.length - 1]?.hour}</span>
      </div>
      <div className="text-[0.6rem] text-muted-foreground mt-1 flex gap-3">
        <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-emerald-500/40 inline-block" /> نشاط</span>
        <span className="inline-flex items-center gap-1"><span className="size-2 rounded-sm bg-sky-400 inline-block" /> مدفوعات</span>
      </div>
    </div>
  );
}

const REASON_STYLE: Record<string, string> = {
  'pay-failed': 'bg-rose-500/10 text-rose-400 ring-rose-500/30',
  'hesitate-price': 'bg-amber-500/10 text-amber-400 ring-amber-500/30',
  'shopping': 'bg-sky-500/10 text-sky-400 ring-sky-500/30',
  'churned': 'bg-slate-500/10 text-slate-300 ring-slate-500/30',
  'human-browser': 'bg-violet-500/10 text-violet-400 ring-violet-500/30',
  'one-shot': 'bg-orange-500/10 text-orange-400 ring-orange-500/30',
  'watch': 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30',
};

export function X402Radar({ analytics, feed, diagnostics }: { analytics: X402Analytics | null; feed: X402FeedEntry[]; diagnostics?: X402WhyNotPaidDiag | null }) {
  const [leadTab, setLeadTab] = useState<'unpaid' | 'viewers'>('unpaid');
  const [boardTab, setBoardTab] = useState<'paid' | 'all'>('all');

  if (!analytics) return null;
  const f = analytics.funnel;
  const paidTotal = f.settled + f.nativeSettled;
  const conversion = f.quotes > 0 ? Math.round((paidTotal / f.quotes) * 100) : 0;
  const leads = leadTab === 'unpaid' ? analytics.unpaid : analytics.viewers;
  const board = boardTab === 'paid' ? analytics.callers.filter(c => c.paid) : analytics.callers;

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-violet-500/5 ring-1 ring-violet-500/20 p-3 text-[0.7rem] text-muted-foreground flex items-start gap-2">
        <Radar className="size-4 text-violet-400 shrink-0 mt-0.5" />
        <span>
          <b className="text-foreground">رادار الوكلاء (v2.17.0) —</b> كل استكشاف لمسارات الاكتشاف الستة، كل طلب سعر 402، وكل تسوية يُسجَّل الآن بايت-ببايت في <Mono>db/x402-requests.json</Mono> — ترى من رأى خدماتك، من طلب سعراً ولم يدفع، وأي الوكلاء أكثر اهتماماً بأي خدمة.
        </span>
      </div>

      {/* 1. FUNNEL */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard icon={Radar} label="اكتشاف الخدمات" accent="violet"
          value={fmtInt(f.discovery)} sub="استكشاف لمسارات الاكتشاف الستة" />
        <StatCard icon={Eye} label="طلبات عرض سعر (402)" accent="gold"
          value={fmtInt(f.quotes)} sub="الوكيل سأل عن السعر ولم يكمل بعد" />
        <StatCard icon={HandCoins} label="مدفوعات ناجحة" accent="emerald"
          value={fmtInt(paidTotal)} sub={`${f.settled} USDC · ${f.nativeSettled} AMAN native`} />
        <StatCard icon={TrendingUp} label="معدل التحويل" accent="sky"
          value={`${conversion}%`} sub="من طلب سعر → دفع فعلاً" />
        <StatCard icon={CircleAlert} label="محاولات فاشلة" accent="rose"
          value={fmtInt(f.verifyFailed)} sub="رفض التحقق قبل تحريك أي أموال" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* 2. MOST WANTED — per-service demand */}
        <div className="glass p-4">
          <div className="text-sm font-bold flex items-center gap-2 mb-3">
            <Flame className="size-4 text-amber-400" /> أكثر الخدمات طلباً
          </div>
          {analytics.perService.length === 0 ? (
            <div className="text-xs text-muted-foreground">لا حركة بعد — أول وكيل يستكشف الكتالوج يبدأ الجدول.</div>
          ) : (
            <div className="space-y-1.5 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 text-[0.58rem] font-black text-muted-foreground px-2 sticky top-0 bg-transparent">
                <span>الخدمة</span><span>أسعار</span><span>مدفوعة</span><span>تحويل</span><span>وكلاء</span>
              </div>
              {analytics.perService.map((s, i) => (
                <div key={s.serviceId} className={`grid grid-cols-[1fr_auto_auto_auto_auto] gap-2 items-center rounded-lg px-2 py-1.5 text-xs ${i === 0 && s.paidTotal > 0 ? 'bg-amber-500/10 ring-1 ring-amber-500/20' : 'bg-muted/40'}`}>
                  <div className="min-w-0">
                    <Mono className="text-[0.62rem] font-bold truncate block" dir="ltr">{s.serviceId}</Mono>
                    <span className="text-[0.56rem] text-muted-foreground">{s.lastAt ? `آخر نشاط ${timeAgo(s.lastAt)}` : ''}</span>
                  </div>
                  <span className="text-amber-400 font-black" title="طلبات عرض سعر">{fmtInt(s.quotes)}</span>
                  <span className="text-emerald-400 font-black" title={`USDC: ${s.settled} · AMAN: ${s.nativeSettled}`}>{fmtInt(s.paidTotal)}</span>
                  <span className={`font-black text-[0.66rem] ${s.conversionPct >= 50 ? 'text-emerald-400' : s.conversionPct > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>{s.conversionPct}%</span>
                  <span className="text-muted-foreground font-bold">{fmtInt(s.uniqueCallers)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 5. 48H PULSE */}
        <div className="glass p-4">
          <div className="text-sm font-bold flex items-center gap-2 mb-3">
            <Activity className="size-4 text-emerald-400" /> نبض آخر 48 ساعة
          </div>
          <PulseBars data={analytics.activity48h} />
        </div>
      </div>

      {/* 3. WARM LEADS — saw but never paid */}
      <div className="glass p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="text-sm font-bold flex items-center gap-2">
            <UserX className="size-4 text-rose-400" /> شاهدوا ولم يدفعوا — عملاء دافئون
          </div>
          <div className="flex rounded-lg ring-1 ring-input p-0.5 gap-0.5 text-[0.62rem] font-bold">
            <button onClick={() => setLeadTab('unpaid')}
              className={`rounded-md px-2.5 py-1 ${leadTab === 'unpaid' ? 'bg-rose-500/15 text-rose-400' : 'text-muted-foreground'}`}>
              طلبوا سعراً ({fmtInt(analytics.unpaid.length)})
            </button>
            <button onClick={() => setLeadTab('viewers')}
              className={`rounded-md px-2.5 py-1 ${leadTab === 'viewers' ? 'bg-violet-500/15 text-violet-400' : 'text-muted-foreground'}`}>
              استكشفوا فقط ({fmtInt(analytics.viewers.length)})
            </button>
          </div>
        </div>
        {leads.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            {leadTab === 'unpaid'
              ? 'لا أحد بعد: كل من طلب سعراً أكمل الدفع — أو لم يطلب أحد سعراً بعد.'
              : 'لا زوار استكشاف حتى الآن خارج جولاتك — انشر الرابط أمام الوكلاء وسيظهر هنا فوراً.'}
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {leads.slice(0, 20).map(c => (
              <div key={c.callerHash} className="rounded-lg bg-muted/40 px-3 py-2 flex items-center justify-between gap-3 text-xs">
                <div className="min-w-0">
                  <div className="font-bold flex items-center gap-1.5 flex-wrap">
                    <ClientKindBadge kind={c.clientKind} />
                    <Mono className="text-[0.62rem]">{c.callerHash}</Mono>
                    {c.topService && c.topService !== '(catalog)' && (
                      <span className="text-[0.6rem] text-muted-foreground">← مهتم بـ <Mono className="text-[0.58rem]">{c.topService}</Mono></span>
                    )}
                  </div>
                  <Mono className="text-[0.58rem] text-muted-foreground block truncate" dir="ltr">{uaShort(c.userAgent)}</Mono>
                </div>
                <div className="flex items-center gap-2 shrink-0 text-[0.62rem] font-bold">
                  {leadTab === 'unpaid' ? (
                    <span className="text-amber-400">{fmtInt(c.quotes)} عرض سعر</span>
                  ) : (
                    <span className="text-violet-400">{fmtInt(c.discoveries)} استكشاف</span>
                  )}
                  <span className="text-muted-foreground">{c.lastAt ? timeAgo(c.lastAt) : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* v2.18.0 — WHY DIDN'T THEY BUY? honest diagnosis + pricing advice */}
      {diagnostics && (diagnostics.clients.length > 0 || diagnostics.services.length > 0) && (
        <div className="glass p-4 space-y-3">
          <div className="text-sm font-bold flex items-center gap-2">
            <Stethoscope className="size-4 text-rose-400" /> لماذا لا يشترون؟ — تشخيص حقيقي من أحداث المتتبع
          </div>
          <div className="rounded-lg bg-rose-500/5 ring-1 ring-rose-500/20 px-3 py-2 text-[0.7rem] text-muted-foreground">{diagnostics.summary}</div>
          {diagnostics.reasonCounts.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {diagnostics.reasonCounts.map(r => (
                <span key={r.code} className={`rounded-full px-2.5 py-1 text-[0.62rem] font-black ring-1 ${REASON_STYLE[r.code] ?? 'bg-muted text-muted-foreground ring-input'}`}>
                  {r.label} · {fmtInt(r.count)}
                </span>
              ))}
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-3">
            {/* per-client diagnosis */}
            <div className="space-y-2">
              <div className="text-[0.66rem] font-black text-muted-foreground">تشخيص العملاء الدافئين</div>
              {diagnostics.clients.slice(0, 8).map(c => (
                <div key={c.callerHash} className="rounded-lg bg-muted/40 px-3 py-2 space-y-1.5 text-xs">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <ClientKindBadge kind={c.clientKind} />
                      <Mono className="text-[0.62rem] font-bold">{c.callerHash}</Mono>
                      <span className="text-[0.6rem] text-muted-foreground">{fmtInt(c.quotes)} عرض سعر</span>
                    </div>
                    <span className="text-[0.58rem] text-muted-foreground">{c.lastAt ? timeAgo(c.lastAt) : ''}</span>
                  </div>
                  {c.reasons.map(r => (
                    <div key={r.code} className="rounded-md bg-background/60 px-2 py-1.5">
                      <span className={`rounded-full px-1.5 py-0.5 text-[0.54rem] font-black ring-1 ${REASON_STYLE[r.code] ?? 'bg-muted text-muted-foreground ring-input'}`}>{r.label}</span>
                      <div className="text-[0.6rem] text-muted-foreground mt-1 leading-relaxed">{r.evidence}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {/* per-service pricing advice */}
            <div className="space-y-2">
              <div className="text-[0.66rem] font-black text-muted-foreground flex items-center gap-1.5"><Scale className="size-3 text-amber-400" /> توصيات لكل خدمة — مبنية على التحويل الحقيقي</div>
              {diagnostics.services.slice(0, 8).map(s => (
                <div key={s.serviceId} className="rounded-lg bg-muted/40 px-3 py-2 space-y-1 text-xs">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Mono className="text-[0.62rem] font-bold" dir="ltr">{s.serviceId}</Mono>
                    <span className="text-[0.58rem] text-muted-foreground">
                      {fmtInt(s.quotes)} سعر · {fmtInt(s.paidTotal)} دفع · {s.conversionPct}%
                      {s.priceAman != null && <> · سعره الحالي <b className="text-foreground" dir="ltr">{s.priceAman} AMAN</b>{s.priceUsd != null && <> (~${s.priceUsd.toFixed(4)})</>}</>}
                    </span>
                  </div>
                  <div className="text-[0.62rem] text-muted-foreground">{s.diagnosis}</div>
                  <div className="text-[0.62rem] font-bold text-amber-400 flex items-start gap-1">
                    <Lightbulb className="size-3 shrink-0 mt-0.5" /> {s.action}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-[0.6rem] text-muted-foreground flex items-start gap-1.5">
            <SearchX className="size-3.5 shrink-0 mt-0.5" />
            <span>التشخيص مستخرج من أحداث حقيقية مسجلة (طلبات الأسعار، محاولات الدفع المرفوضة، الأزمنة) — لا تخمين. قراءة فقط، لا يمس مسار المال أبداً.</span>
          </div>
        </div>
      )}

      {/* 4. AGENT BOARD — most interested callers */}
      <div className="glass p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <div className="text-sm font-bold flex items-center gap-2">
            <Bot className="size-4 text-emerald-400" /> لوحة الوكلاء الأكثر اهتماماً
          </div>
          <div className="flex rounded-lg ring-1 ring-input p-0.5 gap-0.5 text-[0.62rem] font-bold">
            <button onClick={() => setBoardTab('paid')}
              className={`rounded-md px-2.5 py-1 ${boardTab === 'paid' ? 'bg-emerald-500/15 text-emerald-400' : 'text-muted-foreground'}`}>
              دافعون فقط
            </button>
            <button onClick={() => setBoardTab('all')}
              className={`rounded-md px-2.5 py-1 ${boardTab === 'all' ? 'bg-emerald-500/15 text-emerald-400' : 'text-muted-foreground'}`}>
              الجميع ({fmtInt(analytics.callers.length)})
            </button>
          </div>
        </div>
        {board.length === 0 ? (
          <div className="text-xs text-muted-foreground">لا متصلين بعد.</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {board.slice(0, 15).map((c, i) => (
              <div key={c.callerHash} className="rounded-lg bg-muted/40 px-3 py-2 flex items-center justify-between gap-3 text-xs">
                <div className="min-w-0 flex items-center gap-2">
                  <span className={`size-5 shrink-0 rounded-md grid place-items-center text-[0.6rem] font-black ${i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-slate-500/20 text-slate-300' : i === 2 ? 'bg-orange-700/20 text-orange-400' : 'bg-muted text-muted-foreground'}`}>{i + 1}</span>
                  <div className="min-w-0">
                    <div className="font-bold flex items-center gap-1.5 flex-wrap">
                      <ClientKindBadge kind={c.clientKind} />
                      <Mono className="text-[0.62rem]">{c.callerHash}</Mono>
                      {c.paid && <span className="rounded-full bg-emerald-500/10 text-emerald-400 px-2 py-0.5 text-[0.56rem] font-black">دفع فعلاً</span>}
                    </div>
                    <Mono className="text-[0.58rem] text-muted-foreground block truncate" dir="ltr">{uaShort(c.userAgent)}</Mono>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 shrink-0 text-[0.62rem] font-bold">
                  {c.topService && c.topService !== '(catalog)' && (
                    <span className="text-muted-foreground hidden md:inline">← <Mono className="text-[0.58rem]">{c.topService}</Mono></span>
                  )}
                  {c.paidMicros > 0 && <span className="text-sky-400" dir="ltr">${(c.paidMicros / 1e6).toFixed(6)}</span>}
                  {c.paidAman > 0 && <span className="text-emerald-400" dir="ltr">{c.paidAman.toLocaleString('en-US', { maximumFractionDigits: 4 })} AMAN</span>}
                  <span className="text-muted-foreground">{fmtInt(c.hits)} hit</span>
                  <span className="text-muted-foreground">{c.lastAt ? timeAgo(c.lastAt) : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 6. LIVE FEED */}
      <div className="glass p-4">
        <div className="text-sm font-bold flex items-center gap-2 mb-1">
          <Radio className="size-4 text-rose-400" /> البث الحي — كل نداء يُسجَّل لحظياً
        </div>
        <div className="text-[0.62rem] text-muted-foreground mb-3">أحدث {feed.length} حدث من المتتبع الدائم (quotes، settlements، استكشاف الاكتشاف، أخطاء التحقق).</div>
        {feed.length === 0 ? (
          <div className="text-xs text-muted-foreground">لا أحداث بعد.</div>
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto" dir="ltr">
            {feed.map((e, i) => {
              const meta = KIND_META[e.kind] ?? KIND_META.error;
              const Icon = meta.icon;
              const paid = e.kind === 'settled' || e.kind === 'native-settled';
              return (
                <div key={`${e.ts}-${i}`} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-2.5 py-1.5 text-[0.66rem]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`rounded-full px-2 py-0.5 text-[0.56rem] font-black inline-flex items-center gap-1 ${meta.cls}`}>
                      <Icon className="size-2.5" /> {meta.label}
                    </span>
                    {e.surface ? (
                      <Mono className="text-[0.6rem] text-violet-400 truncate">{SURFACE_LABEL[e.surface] ?? e.surface}</Mono>
                    ) : (
                      <Mono className="text-[0.6rem] font-bold truncate">{e.serviceId ?? '(catalog)'}</Mono>
                    )}
                    {paid && (
                      <span className="font-black text-emerald-400 whitespace-nowrap" dir="ltr">
                        {e.kind === 'settled' ? `$${((Number(e.amountMicros ?? 0)) / 1e6).toFixed(6)}` : `${(e.amountAman ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 })} AMAN`}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0 text-muted-foreground">
                    <ClientKindBadge kind={e.clientKind} />
                    {e.callerHash && <Mono className="text-[0.56rem]">{e.callerHash}</Mono>}
                    <span className="text-[0.56rem]">{timeAgo(e.ts)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* surfaces reference */}
      <div className="glass p-4">
        <div className="text-sm font-bold flex items-center gap-2 mb-2">
          <Globe className="size-4 text-sky-400" /> مسارات الاكتشاف المراقبة — عين الرادار
        </div>
        <div className="text-[0.66rem] text-muted-foreground mb-2">أي وكيل خارجي يجلب أحد هذه المسارات يُسجَّل: نوع العميل (وكيل/متصفح)، بصمة المصدر، والوقت.</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(SURFACE_LABEL).map(([surface, label]) => (
            <div key={surface} className="rounded-lg bg-muted/40 px-2.5 py-1.5">
              <Mono className="text-[0.6rem] font-bold" dir="ltr">{label}</Mono>
              <div className="text-[0.56rem] text-muted-foreground">{surface}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
